/**
 * OpponentScout — This Week's Opponent Breakdown (Analysers rebuild, Stage 2)
 *
 * Every number here is real: real opponent, real roster, real win
 * probability (the SAME number League Home's matchup card shows — both call
 * projectMatchupWithSchedule server-side, so they can never disagree — see
 * leagueController.js's getOpponentScout). Category strength and key-threat
 * rankings are code-computed against the whole readable league; strengths/
 * weaknesses/game-plan prose is one validated Claude call explaining those
 * numbers, never inventing its own. No hardcoded preview/mock data path —
 * the old page's entire top section was hardcoded literals (see the
 * Analysers rebuild prompt's Directional Change 1); this is the real thing.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import AppShell from '../components/AppShell'
import ToolHeader from '../components/ToolHeader'
import JaiRichText from '../components/JaiRichText'
import PlayerPhoto from '../components/PlayerPhoto'
import PlayerProfileModal from '../components/PlayerProfileModal'
import StickyAskBar from '../components/StickyAskBar'
import UpgradePrompt from '../components/UpgradePrompt'
import TeamChip from '../components/TeamChip'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Card, StatStrip, V2Bar, DarkPlanCard, PosChip, solidChip, pctTone, UpdatedRow, LiveScoreBar, Notice } from '../components/JaiV2'
import { JAIMascot } from '../components/JAIMascot'
import { STAT_GLYPHS } from '../config/statGlyphs'
import { addNote } from '../services/notesStore'

// ── Small shared bits ───────────────────────────────────────────────────

const THREAT_META = {
  high:   { color: V2.error,   label: 'HIGH' },
  medium: { color: V2.warning, label: 'MED' },
  low:    { color: V2.faint,   label: 'LOW' },
}

const PLAN_TONES = {
  attack: { color: V2.success, label: 'ATTACK' },
  defend: { color: V2.error,   label: 'DEFEND' },
  watch:  { color: V2.warning, label: 'WATCH' },
}

const NO_OPPONENT_MESSAGE = {
  'draft-incomplete': "Opponent Scout needs a completed draft — there's no real roster to scout yet.",
  'no-opponents': "No head-to-head matchup this week — nothing for JAI to scout.",
  'no-games': "The season hasn't started yet, so there are no games to scout. This fills in once your first matchup week begins.",
}

// ── Key Threats — full width, one row per player ────────────────────────

function ThreatRow({ threat, sport, isLast, onSelect }) {
  const meta = THREAT_META[threat.threat] || THREAT_META.low
  const injured = threat.injuryStatus && threat.injuryStatus !== 'ACTIVE'

  return (
    <button
      onClick={() => onSelect(threat)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '13px 20px', width: '100%',
        borderTop: 'none', borderLeft: 'none', borderRight: 'none',
        borderBottom: isLast ? 'none' : '1px solid rgba(10,31,68,0.06)', flexWrap: 'wrap',
        background: 'none', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
        transition: 'background 150ms',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(10,31,68,0.02)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'none' }}
    >
      <PlayerPhoto name={threat.name} size={32} rounded="full" />
      <span style={{ fontSize: 14, fontWeight: 700, color: V2.navy, minWidth: 120 }}>
        {threat.name}
        {/* Max one glyph per row (Hard Rule 4) — injury is the only flag that applies here. */}
        {injured && <span style={{ marginLeft: 6 }}>{STAT_GLYPHS.injury}</span>}
      </span>
      <PosChip>{threat.position}</PosChip>
      <TeamChip abbr={threat.team} sport={sport} />

      <div style={{ flex: 1, minWidth: 100, display: 'flex', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        {threat.topCategories?.length > 0 ? (
          // These are scoring-category values (FG%/FT% etc in a categories
          // league), not standalone metrics — left uncoloured per the
          // percentage-colouring brief's exclusion for fantasy stat columns.
          threat.topCategories.map((c) => (
            <span key={c.key} style={{ fontSize: 11.5, fontFamily: JAI_FONTS.mono, color: V2.mute, whiteSpace: 'nowrap' }}>
              {c.key} <strong style={{ color: V2.navy }}>{c.value}</strong> <span style={{ color: V2.faint }}>#{c.leagueRank}</span>
            </span>
          ))
        ) : threat.avgFantasyPoints != null ? (
          <span style={{ fontSize: 12.5, fontFamily: JAI_FONTS.mono, color: V2.mute, whiteSpace: 'nowrap' }}>
            {threat.avgFantasyPoints.toFixed(1)} pts/gm
          </span>
        ) : null}
      </div>

      <span style={{
        fontSize: 9.5, fontWeight: 800, fontFamily: JAI_FONTS.mono, letterSpacing: '0.05em',
        ...solidChip(meta.color), width: 42, textAlign: 'center', borderRadius: 5, padding: '3px 0', flexShrink: 0,
      }}>
        {meta.label}
      </span>
    </button>
  )
}

function KeyThreatsCard({ keyThreats, sport, onSelectThreat }) {
  if (!keyThreats?.length) return null
  return (
    <V2Card pad="0" style={{ marginBottom: 16 }}>
      <div style={{ padding: '13px 20px', borderBottom: '1px solid rgba(10,31,68,0.07)', fontSize: 13.5, fontWeight: 800, color: V2.navy, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        Key threats
      </div>
      {keyThreats.map((t, i) => (
        <ThreatRow key={t.playerId ?? t.name} threat={t} sport={sport} isLast={i === keyThreats.length - 1} onSelect={onSelectThreat} />
      ))}
    </V2Card>
  )
}

// ── Category Strength — bars, only for H2H-categories leagues ──────────

function CategoryStrengthCard({ categoryStrength, teamCount }) {
  if (!categoryStrength?.length) return null
  const safeTeamCount = teamCount > 1 ? teamCount : categoryStrength.length || 1

  return (
    <V2Card pad="18px 22px" style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 13.5, fontWeight: 800, color: V2.navy, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 14 }}>Their category strength</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
        {categoryStrength.map((cat) => {
          // Bar width comes from the REAL leagueRank against the real team
          // count — never a hardcoded percentage (see the old page's
          // STRENGTH_TONES, which this replaces).
          // NOTE: this colours the opponent's LEAGUE RANK in this category,
          // not a raw percentage — it's exempt from the percentage-colouring
          // brief's pctTone() rule for that reason. Don't "fix" it to pctTone.
          const pct = Math.round(((safeTeamCount - cat.leagueRank + 1) / safeTeamCount) * 100)
          const tone = cat.tone === 'strong' ? 'strong' : cat.tone === 'weak' ? 'weak' : 'neutral'
          const noteColor = cat.tone === 'strong' ? V2.success : cat.tone === 'weak' ? V2.error : V2.faint
          return (
            <div key={cat.key} style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
              <span style={{ width: 40, fontSize: 12, fontWeight: 800, color: V2.navy, fontFamily: JAI_FONTS.mono, flexShrink: 0 }}>{cat.key}</span>
              <V2Bar value={pct} tone={tone} style={{ flex: 1 }} />
              <span style={{ width: 96, fontSize: 11.5, color: noteColor, fontWeight: 600, textAlign: 'right', flexShrink: 0 }}>
                #{cat.leagueRank} of {safeTeamCount} · {cat.theirAvg}
              </span>
            </div>
          )
        })}
      </div>
    </V2Card>
  )
}

// ── Strengths / Weaknesses — two columns ────────────────────────────────

function StrengthsWeaknesses({ strengths, weaknesses }) {
  if (!strengths?.length && !weaknesses?.length) return null
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
      {strengths?.length > 0 && (
        <V2Card pad="16px 18px">
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: V2.success, marginBottom: 10 }}>
            Their strengths
          </div>
          <JaiRichText content={strengths.map((s) => `- ${s}`).join('\n')} style={{ fontSize: 13 }} />
        </V2Card>
      )}
      {weaknesses?.length > 0 && (
        <V2Card pad="16px 18px">
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: V2.error, marginBottom: 10 }}>
            Their weaknesses
          </div>
          <JaiRichText content={weaknesses.map((w) => `- ${w}`).join('\n')} style={{ fontSize: 13 }} />
        </V2Card>
      )}
    </div>
  )
}

// ── Attack / Defend / Watch ──────────────────────────────────────────────

function GamePlanCards({ plan }) {
  if (!plan?.length) return null
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
      {plan.map((item, i) => {
        const tone = PLAN_TONES[item.type] || PLAN_TONES.watch
        return (
          <V2Card key={i} pad="14px 18px" style={{ borderLeft: `3px solid ${tone.color}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 5 }}>
              <span style={{ color: tone.color, fontSize: 10, fontWeight: 800, fontFamily: JAI_FONTS.mono, letterSpacing: '0.1em' }}>{tone.label}</span>
              <span style={{ color: V2.navy, fontSize: 14, fontWeight: 700 }}>{item.title}</span>
            </div>
            <p style={{ color: V2.mute, fontSize: 13, lineHeight: 1.6, margin: 0 }}>{item.detail}</p>
          </V2Card>
        )
      })}
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────

export default function OpponentScout() {
  const { id } = useParams()

  const [league, setLeague] = useState(null)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [upgrade, setUpgrade] = useState(null)
  const [noteSaved, setNoteSaved] = useState(false)
  const [noteSaving, setNoteSaving] = useState(false)
  const [noteError, setNoteError] = useState('')
  const [openThreat, setOpenThreat] = useState(null)

  const sport = (league?.sport || 'nba').toLowerCase()

  const load = useCallback(async ({ refresh = false } = {}) => {
    if (refresh) setRefreshing(true)
    setError('')
    setUpgrade(null)
    try {
      const res = await leaguesApi.getOpponentScout(id, { refresh })
      setData(res.data)
      setNoteSaved(false)
    } catch (err) {
      if (err.response?.data?.code === 'PLAN_UPGRADE_REQUIRED') {
        setUpgrade({ tool: err.response.data.requiredTool, message: err.response.data.error })
      } else {
        setError(err.response?.data?.error || "JAI couldn't load this week's opponent right now. Try again in a minute.")
      }
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [id])

  useEffect(() => {
    let cancelled = false
    leaguesApi.get(id).then((r) => { if (!cancelled) setLeague(r.data.league) }).catch(() => {})
    load()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const opponentName = data?.available ? data.opponent?.teamName : null
  const managerName = data?.available ? data.opponent?.managerName : null
  const subtitle = data?.available
    ? `JAI's game plan for beating ${opponentName}${managerName ? ` (${managerName})` : ''} this week.`
    : (loading ? 'Scouting this week\'s opponent…' : undefined)

  const askBarContext = useMemo(() => {
    if (!data?.available) return undefined
    const threatsText = (data.keyThreats || [])
      .map((t) => `- ${t.name} (${t.position || '?'}, ${t.team || '?'}) — threat: ${t.threat}`)
      .join('\n')
    const planText = (data.plan || []).map((p) => `- [${p.type}] ${p.title}: ${p.detail}`).join('\n')
    return `THE USER IS LOOKING AT THIS WEEK'S OPPONENT SCOUT REPORT RIGHT NOW.\n` +
      `Opponent: ${opponentName}\nWin probability: ${data.winProbability}%\n` +
      `Key threats:\n${threatsText || 'None'}\nGame plan:\n${planText || 'None'}\nJAI's plan: ${data.jaiPlan}`
  }, [data, opponentName])

  const saveToNotes = async () => {
    if (noteSaved || noteSaving || !data?.jaiPlan) return
    setNoteSaving(true)
    setNoteError('')
    try {
      await addNote(id, data.jaiPlan, {
        source: 'opponent-scout', pinned: true,
        title: opponentName ? `Game plan vs ${opponentName}` : 'This week\'s opponent game plan',
      })
      setNoteSaved(true)
    } catch {
      setNoteError('Could not save this to your notes. Try again.')
    } finally {
      setNoteSaving(false)
    }
  }

  const projectionCell = !data?.available
    ? '—'
    : data.format === 'categories'
      ? (data.projection?.projectedCategoryRecord ?? '—')
      : (data.projection?.me != null && data.projection?.them != null
          ? `${data.projection.me.toFixed(1)}–${data.projection.them.toFixed(1)}`
          : '—')

  return (
    <AppShell>
      <div style={{ padding: '30px 40px', fontFamily: JAI_FONTS.tight }}>
        <div style={{ maxWidth: 880, margin: '0 auto', paddingBottom: 100 }}>
          <ToolHeader
            leagueName={league?.name}
            title="Opponent Scout"
            subtitle={subtitle}
            back={{ label: 'Go to League Home Screen', to: `/league/${id}` }}
          />

          {/* StatStrip, top-centre, directly under the title */}
          {data?.available && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 8 }}>
              <StatStrip cells={[
                [`${data.winProbability}%`, 'Win Prob', pctTone(data.winProbability)],
                [projectionCell, 'JAI Projection'],
                [data.opponent?.record || '—', 'Their Record'],
                [data.opponent?.rank ? `#${data.opponent.rank}` : '—', 'Their Rank'],
              ]} />
            </div>
          )}

          {/* Live score — a real scoreboard, kept visually distinct from the
              JAI Projection cell in the StatStrip above so the two numbers
              (what's actually happening vs. what JAI expects) can never be
              confused for each other. */}
          {data?.available && data.live && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <LiveScoreBar
                myTeamName={league?.teamName || 'Your team'}
                myPoints={data.live.myPoints}
                theirTeamName={opponentName || 'Opponent'}
                theirPoints={data.live.theirPoints}
              />
            </div>
          )}

          {/* asOf + refresh — small chrome, deliberately quieter than the score above */}
          {data?.available && (
            <div style={{ marginBottom: 24 }}>
              <UpdatedRow asOf={data.asOf} onRefresh={() => load({ refresh: true })} refreshing={refreshing} credits="opponentScout" />
            </div>
          )}

          {upgrade && <UpgradePrompt tool={upgrade.tool} message={upgrade.message} />}

          {/* Error banner */}
          {error && !upgrade && (
            <Notice tone="error" action="Try again" onAction={() => load()}>{error}</Notice>
          )}

          {/* Inline spinner while the first load is in flight — never a full-page loader */}
          {loading && !error && !upgrade && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 0', gap: 12 }}>
              <JAIMascot size={56} state="thinking" animate={true} />
              <p style={{ fontSize: 13.5, color: V2.faint, margin: 0 }}>Scouting this week's opponent…</p>
            </div>
          )}

          {/* Honest no-opponent / pre-draft state — never an empty grid */}
          {!loading && !error && data && !data.available && (
            <V2Card pad="32px 24px" style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 13.5, color: 'rgba(10,31,68,0.72)', margin: 0 }}>
                {NO_OPPONENT_MESSAGE[data.reason] || "JAI can't scout an opponent for this league right now."}
              </p>
            </V2Card>
          )}

          {/* Real content */}
          {!loading && !error && data?.available && (
            <>
              <KeyThreatsCard keyThreats={data.keyThreats} sport={sport} onSelectThreat={setOpenThreat} />
              <CategoryStrengthCard categoryStrength={data.categoryStrength} teamCount={league?.teamCount} />
              <StrengthsWeaknesses strengths={data.strengths} weaknesses={data.weaknesses} />
              <GamePlanCards plan={data.plan} />

              {data.jaiPlan && (
                <DarkPlanCard
                  title="JAI's plan"
                  linkLabel={noteSaved ? 'Saved to My Notes ✓' : noteSaving ? 'Saving…' : (noteError || 'Save to My Notes →')}
                  onLink={saveToNotes}
                  style={{ marginBottom: 16 }}
                >
                  <JaiRichText content={data.jaiPlan} dark style={{ fontSize: 15 }} />
                </DarkPlanCard>
              )}
            </>
          )}
        </div>
      </div>

      <StickyAskBar
        leagueId={id}
        scope="opponent"
        context={askBarContext}
        placeholder="Ask JAI about this week's matchup"
      />

      {openThreat && (
        <PlayerProfileModal
          player={openThreat}
          sport={sport}
          stats={{ season: openThreat.seasonStats }}
          onClose={() => setOpenThreat(null)}
        />
      )}
    </AppShell>
  )
}
